import { FC } from 'react';
import { FormControlLabel, Switch, SwitchProps } from '@mui/material';
import { styled } from '@mui/material/styles';

import Tooltip from '../Tooltip';
import { ToggleProps } from './types';

const MuiSwitch = styled((props: SwitchProps & ToggleProps) => (
  <Switch
    onClick={e => e.stopPropagation()}
    focusVisibleClassName='.Mui-focusVisible'
    disableRipple
    {...props}
  />
))(({ checked, disabled, theme, variant }) => ({
  '& .MuiSwitch-switchBase': {
    '& .MuiSwitch-input': {
      height: '20px !important',
      left: '-200%',
      width: '600%',
    },
    '&.Mui-checked': {
      '& + .MuiSwitch-track': {
        backgroundColor:
          variant === 'destructive'
            ? theme.palette.colors.red[500]
            : theme.palette.colors.green[500],
        border: 0,
        opacity: 1,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        backgroundColor: theme.palette.colors.green[300],
        opacity: 1,
      },
      color: theme.palette.colors.white,
      transform: 'translateX(32px)',
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'inherit',
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      backgroundColor: theme.palette.colors.slate[100],
      opacity: 1,
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: checked
        ? theme.palette.colors.white
        : theme.palette.colors.grey[300],
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      border: `6px solid ${theme.palette.colors.white}`,
      color: theme.palette.colors.green[400],
    },
    margin: 2,
    padding: 0,
    transitionDuration: '300ms',
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    height: 16,
    left: '2px',
    position: 'relative',
    top: '2px',
    width: 16,
  },
  '& .MuiSwitch-track': {
    '&:after': {
      ...theme.typography.font14Bold,
      color: disabled
        ? theme.palette.colors.grey[400]
        : theme.palette.colors.white,
      content: '"Off"',
      position: 'absolute',
      right: '8px',
      top: '2px',
      visibility: checked ? 'hidden' : 'visible',
    },
    '&:before': {
      ...theme.typography.font14Bold,
      color: theme.palette.colors.white,
      content: '"On"',
      left: '8px',
      position: 'absolute',
      top: '2px',
      visibility: checked ? 'visible' : 'hidden',
    },
    backgroundColor:
      variant === 'destructive'
        ? theme.palette.colors.red[500]
        : theme.palette.colors.grey[500],
    borderRadius: 26 / 2,
    opacity: 1,
    position: 'relative',
    transition: theme.transitions.create(['background-color'], {
      duration: 800,
    }),
  },
  height: 24,
  padding: 0,
  width: 56,
  ...(disabled
    ? {}
    : {
        '&:hover': {
          '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
              '& + .MuiSwitch-track': {
                backgroundColor:
                  variant === 'destructive'
                    ? theme.palette.colors.red[500]
                    : theme.palette.colors.green[600],
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor:
              variant === 'destructive'
                ? theme.palette.colors.red[500]
                : theme.palette.colors.grey[600],
          },
        },
      }),
}));

const Toggle: FC<ToggleProps> = ({
  ariaLabel = '',
  label = '',
  tooltipText = '',
  ...rest
}) => {
  const labeledToggle = tooltipText ? (
    <Tooltip tooltipContent={tooltipText}>
      <MuiSwitch sx={{ mr: 1.5 }} {...rest} />
    </Tooltip>
  ) : (
    <MuiSwitch sx={{ mr: 1.5 }} {...rest} />
  );

  const unlabeledTogggle = tooltipText ? (
    <Tooltip tooltipContent={tooltipText}>
      <MuiSwitch inputProps={{ 'aria-label': ariaLabel }} {...rest} />
    </Tooltip>
  ) : (
    <MuiSwitch inputProps={{ 'aria-label': ariaLabel }} {...rest} />
  );

  return label ? (
    <FormControlLabel
      control={labeledToggle}
      label={label}
      sx={{ margin: 0 }}
    />
  ) : (
    unlabeledTogggle
  );
};

export default Toggle;
